import Grid from '@material-ui/core/Grid';
import GridFormField from 'shared/components/GridFormField';
import IconButton from '@material-ui/core/IconButton';
import React from 'react';
import removeHTML from 'shared/utils/removeHTML';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {
	ageInRange,
	emailValidation,
	exactStrLength,
	maxStrLength,
	settingValidation,
	restrictedTextInputValidation,
	isAfterDobCoApp,
	isValidDate,
	zipPostalValidation,
} from 'shared/utils/validations';
import {
	formatSsn,
	formatSsnHidden,
	normalizeSsn,
	normalizeSsnHidden,
	formatZipPostal,
	normalizeZipPostal,
} from 'shared/utils/formatting';
import Typography from '@material-ui/core/Typography';
import { FormSection } from 'redux-form';
import InputAdornment from '@material-ui/core/InputAdornment';
import { PropTypes } from 'prop-types';
import { COAPP_PERSONAL_INFORMATION, STATES } from './constants';

class CoAppPersonalInformationFields extends React.Component {
	constructor() {
		super();
		this.state = { showSsn: false };
		this.handleClickShowSsn = this.handleClickShowSsn.bind(this);
		this.formatSsnField = this.formatSsnField.bind(this);
		this.normalizeSsnField = this.normalizeSsnField.bind(this);
		this.renderSsnIcon = this.renderSsnIcon.bind(this);
		this.formatZipPostalField = this.formatZipPostalField.bind(this);
		this.normalizeZipPostalField = this.normalizeZipPostalField.bind(this);
	}

	handleClickShowSsn() {
		this.setState({ showSsn: !this.state.showSsn });
	}

	formatSsnField(value) {
		if (this.state.showSsn) {
			return formatSsn(value);
		}
		return formatSsnHidden(value);
	}

	normalizeSsnField(value, previousValue) {
		if (this.state.showSsn) {
			return normalizeSsn(value);
		}
		return normalizeSsnHidden(value, previousValue);
	}

	// eslint-disable-next-line class-methods-use-this
	formatZipPostalField(value) {
		return formatZipPostal(value);
	}

	// eslint-disable-next-line class-methods-use-this
	normalizeZipPostalField(value) {
		return normalizeZipPostal(value);
	}

	renderSsnIcon() {
		if (this.state.showSsn) {
			return <VisibilityOff />;
		}
		return <Visibility />;
	}

	render() {
		const { disableAll, settings, name } = this.props;
		const maxFirstNameLength = 20;
		const maxLastNameLength = 50;
		return (
			<FormSection name={name} style={{ width: '100%' }}>
				<Grid container spacing={5}>
					<GridFormField
						fieldSettings={settings.coApp.name}
						name="firstName"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppFirstNameLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppFirstNameDescription}
						validation={[
							settingValidation(settings.coApp.name),
							maxStrLength(maxFirstNameLength),
							restrictedTextInputValidation,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.name}
						name="lastName"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppLastNameLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppLastNameDescription}
						validation={[
							settingValidation(settings.coApp.name),
							maxStrLength(maxLastNameLength),
							restrictedTextInputValidation,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.address1}
						name="address1"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppAddress1Label}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppAddress1Description}
						validation={[settingValidation(settings.coApp.address1), maxStrLength(30)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.address2}
						name="address2"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppAddress2Label}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppAddress2Description}
						validation={[settingValidation(settings.coApp.address2), maxStrLength(30)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.city}
						name="city"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppCityLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppCityDescription}
						validation={[settingValidation(settings.coApp.city), maxStrLength(30)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.state}
						name="state"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppStateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppStateDescription}
						gridItemSize={{ xs: 8, md: 4 }}
						select
						menuOptions={STATES}
						validation={settingValidation(settings.coApp.state)}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.zip}
						name="zipCode"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppPostalLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppPostalDescription}
						gridItemSize={{ xs: 4, md: 2 }}
						validation={[
							zipPostalValidation,
							settingValidation(settings.coApp.zip),
							maxStrLength(10),
						]}
						format={this.formatZipPostalField}
						normalize={this.normalizeZipPostalField}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppSsnLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppSsnDescription}
						fieldSettings={settings.coApp.socialSecurityNumber}
						name="ssn"
						validation={[settingValidation(settings.coApp.socialSecurityNumber), exactStrLength(9)]}
						disabled={disableAll}
						format={this.formatSsnField}
						normalize={this.normalizeSsnField}
						endAdornment={
							<InputAdornment position="end">
								<IconButton
									onClick={this.handleClickShowSsn}
									onMouseDown={e => e.preventDefault()} // Don't change focused field when pressing button
								>
									{this.renderSsnIcon()}
								</IconButton>
							</InputAdornment>
						}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDriversLicenseLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDriversLicenseDescription}
						fieldSettings={settings.coApp.driversLicenseNumber}
						name="driversLicense"
						validation={[settingValidation(settings.coApp.driversLicenseNumber), maxStrLength(25)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.coApp.driversLicenseState}
						name="driversLicenseState"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDriversLicenseStateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDriversLicenseStateDescription}
						select
						menuOptions={STATES}
						validation={settingValidation(settings.coApp.driversLicenseState)}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDriversLicenseExpDateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDriversLicenseExpDateDescription}
						fieldSettings={settings.coApp.driversLicenseExpDate}
						name="driversLicenseExpDate"
						type="date"
						inputProps={{
							style: { textTransform: 'uppercase', marginTop: '16px' },
							placeholder: 'MM/dd/yyyy',
						}}
						labelShrink
						validation={[
							settingValidation(settings.coApp.driversLicenseExpDate),
							isValidDate,
							isAfterDobCoApp,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDateOfBirthLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppDateOfBirthDescription}
						fieldSettings={settings.coApp.birthdate}
						name="dob"
						type="date"
						inputProps={{
							style: { textTransform: 'uppercase', marginTop: '16px' },
							placeholder: 'MM/dd/yyyy',
						}}
						labelShrink
						validation={[settingValidation(settings.coApp.birthdate), ageInRange(18, 110)]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppEmailLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppEmailDescription}
						fieldSettings={settings.coApp.emailAddress}
						name="emailAddress"
						validation={[
							settingValidation(settings.coApp.emailAddress),
							emailValidation,
							maxStrLength(50),
						]}
						disabled={disableAll}
					/>
				</Grid>
				<Typography type="caption" component="div" style={{ paddingTop: '20px' }}>
					<div
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: removeHTML(settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppPersonalInformationFooter),
						}}
					/>
				</Typography>
			</FormSection>
		);
	}
}

CoAppPersonalInformationFields.propTypes = {
	disableAll: PropTypes.bool,
	settings: PropTypes.shape({
		primary: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					fieldId: PropTypes.string,
					fieldStatus: PropTypes.string,
					fieldSubHeaderType: PropTypes.string,
				}),
				PropTypes.number,
			]),
		),
	}),
	name: PropTypes.string,
};

CoAppPersonalInformationFields.defaultProps = {
	disableAll: false,
	settings: {},
	name: COAPP_PERSONAL_INFORMATION,
};

export default CoAppPersonalInformationFields;
